<template>
    <main
        class="min-w-0 flex-1 border-t border-taupe-200 lg:flex overflow-y-auto bg-taupe-50"
    >
        <!-- Primary column -->
        <section
            aria-labelledby="primary-heading"
            class="flex h-full min-w-0 flex-1 flex-col lg:order-last pt-6 sm:pt-8 lg:pt-10 xl:max-w-7xl xl:mx-auto w-full"
        >
            <h1 id="primary-heading" class="sr-only">Admin: Teams</h1>
            <div class="px-4 sm:px-6 lg:px-8 pb-12 space-y-8">
                <!-- Header with tabs and action button -->
                <div class="mb-6">
                    <admin-header-tabs current="teams" />
                </div>

                <!-- Page Title and Action -->
                <div
                    class="flex items-center justify-between mb-6 min-h-[2.5rem]"
                >
                    <h2 class="text-2xl font-medium text-taupe-900">Teams</h2>
                    <div class="flex-shrink-0">
                        <zen-button
                            v-if="!loading && can(PERMISSIONS.TEAM_CREATE)"
                            variant="primary"
                            size="medium"
                            data-testid="create-team-open-button"
                            @click="openCreateModal()"
                        >
                            <template #icon>
                                <svg
                                    class="h-5 w-5"
                                    viewBox="0 0 20 20"
                                    fill="currentColor"
                                    aria-hidden="true"
                                >
                                    <path
                                        d="M10.75 4.75a.75.75 0 00-1.5 0v4.5h-4.5a.75.75 0 000 1.5h4.5v4.5a.75.75 0 001.5 0v-4.5h4.5a.75.75 0 000-1.5h-4.5v-4.5z"
                                    />
                                </svg>
                            </template>
                            Create Team
                        </zen-button>
                    </div>
                </div>

                <zen-loading-indicator v-if="loading" />

                <zen-empty-table-message
                    v-if="!loading && teams.length === 0"
                    title="No teams yet"
                    icon="ZenIconUsers"
                >
                    There are no teams.
                </zen-empty-table-message>

                <!-- Teams Table -->
                <zen-grid v-if="!loading && teams.length > 0">
                    <zen-grid-table>
                        <zen-grid-header>
                            <zen-grid-header-row>
                                <zen-grid-header-cell
                                    :hidable="false"
                                    title="Name"
                                />
                                <zen-grid-header-cell title="Description" />
                                <zen-grid-header-cell title="Grants" />
                                <zen-grid-header-cell title="Members" />
                                <zen-grid-header-cell
                                    alignment="right"
                                    title="Actions"
                                />
                            </zen-grid-header-row>
                        </zen-grid-header>
                        <zen-grid-body>
                            <zen-grid-body-row
                                v-for="team in teams"
                                :key="team.ulid"
                            >
                                <zen-grid-body-cell
                                    :hidable="false"
                                    :primary="true"
                                >
                                    <router-link
                                        class="font-medium text-sky-600 hover:text-sky-500"
                                        :to="{
                                            name: 'AdminTeamDetailsScreen',
                                            params: {
                                                teamUlid: team.ulid,
                                            },
                                        }"
                                    >
                                        {{ team.name }}
                                    </router-link>
                                    <zen-grid-mobile-detail>
                                        <zen-grid-mobile-detail-item
                                            label="Grants"
                                        >
                                            <div
                                                v-if="
                                                    (
                                                        teamGrants[team.ulid] ??
                                                        []
                                                    ).length
                                                "
                                                class="flex flex-col gap-2"
                                            >
                                                <div
                                                    v-for="grant in teamGrants[
                                                        team.ulid
                                                    ] ?? []"
                                                    :key="grant.ulid"
                                                    class="inline-flex items-center gap-1.5 w-fit whitespace-nowrap"
                                                >
                                                    <zen-inline-pill
                                                        :value="grant.role"
                                                    />
                                                    <span
                                                        class="text-xs text-taupe-600"
                                                        >@
                                                        {{
                                                            scopeLabel(grant)
                                                        }}</span
                                                    >
                                                </div>
                                            </div>
                                            <span v-else class="text-taupe-400"
                                                >—</span
                                            >
                                        </zen-grid-mobile-detail-item>
                                        <zen-grid-mobile-detail-item
                                            label="Description"
                                        >
                                            {{ team.description || '—' }}
                                        </zen-grid-mobile-detail-item>
                                        <zen-grid-mobile-detail-item
                                            label="Members"
                                        >
                                            {{ team.users?.data?.length || 0 }}
                                            {{
                                                (team.users?.data?.length ||
                                                    0) === 1
                                                    ? 'member'
                                                    : 'members'
                                            }}
                                        </zen-grid-mobile-detail-item>
                                        <div class="flex gap-1 pt-1">
                                            <zen-table-icon
                                                v-if="isAdmin"
                                                @click="openGrantsPanel(team)"
                                                :srText="
                                                    'Manage grants for ' +
                                                    team.name
                                                "
                                                tooltipText="Manage Grants"
                                            >
                                                <svg
                                                    class="h-6 w-6"
                                                    viewBox="0 0 24 24"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    stroke-width="1.5"
                                                    stroke-linecap="round"
                                                    stroke-linejoin="round"
                                                >
                                                    <path
                                                        d="M16.5 10.5V6.75a4.5 4.5 0 1 0-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 0 0 2.25-2.25v-6.75a2.25 2.25 0 0 0-2.25-2.25H6.75a2.25 2.25 0 0 0-2.25 2.25v6.75a2.25 2.25 0 0 0 2.25 2.25Z"
                                                    />
                                                </svg>
                                            </zen-table-icon>
                                            <zen-table-icon
                                                v-if="
                                                    can(PERMISSIONS.TEAM_DELETE)
                                                "
                                                @click="
                                                    openDeleteModal(team.ulid)
                                                "
                                                :srText="'Delete ' + team.name"
                                                tooltipText="Delete"
                                            >
                                                <zen-icon-remove />
                                            </zen-table-icon>
                                        </div>
                                    </zen-grid-mobile-detail>
                                </zen-grid-body-cell>
                                <zen-grid-body-cell>
                                    <span class="text-taupe-500">{{
                                        team.description || '—'
                                    }}</span>
                                </zen-grid-body-cell>
                                <zen-grid-body-cell>
                                    <div class="flex flex-col gap-2">
                                        <div
                                            v-for="grant in teamGrants[
                                                team.ulid
                                            ] ?? []"
                                            :key="grant.ulid"
                                            class="inline-flex items-center gap-2 w-fit whitespace-nowrap"
                                        >
                                            <zen-inline-pill
                                                :value="grant.role"
                                            />
                                            <span class="text-sm text-taupe-600"
                                                >@ {{ scopeLabel(grant) }}</span
                                            >
                                        </div>
                                        <span
                                            v-if="
                                                !(teamGrants[team.ulid] ?? [])
                                                    .length
                                            "
                                            class="text-sm text-taupe-400"
                                        >
                                            —
                                        </span>
                                    </div>
                                </zen-grid-body-cell>
                                <zen-grid-body-cell>
                                    <span class="text-taupe-900">{{
                                        team.users?.data?.length || 0
                                    }}</span>
                                </zen-grid-body-cell>
                                <zen-grid-body-cell alignment="right">
                                    <div class="flex justify-end gap-2">
                                        <zen-table-icon
                                            v-if="isAdmin"
                                            @click="openGrantsPanel(team)"
                                            :srText="
                                                'Manage grants for ' + team.name
                                            "
                                            tooltipText="Manage Grants"
                                        >
                                            <svg
                                                class="h-6 w-6"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                stroke="currentColor"
                                                stroke-width="1.5"
                                                stroke-linecap="round"
                                                stroke-linejoin="round"
                                            >
                                                <path
                                                    d="M16.5 10.5V6.75a4.5 4.5 0 1 0-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 0 0 2.25-2.25v-6.75a2.25 2.25 0 0 0-2.25-2.25H6.75a2.25 2.25 0 0 0-2.25 2.25v6.75a2.25 2.25 0 0 0 2.25 2.25Z"
                                                />
                                            </svg>
                                        </zen-table-icon>
                                        <zen-table-icon
                                            v-if="can(PERMISSIONS.TEAM_DELETE)"
                                            @click="openDeleteModal(team.ulid)"
                                            :srText="'Delete ' + team.name"
                                            tooltipText="Delete"
                                        >
                                            <zen-icon-remove />
                                        </zen-table-icon>
                                    </div>
                                </zen-grid-body-cell>
                            </zen-grid-body-row>
                        </zen-grid-body>
                    </zen-grid-table>
                </zen-grid>
            </div>
        </section>

        <delete-team-modal
            :is-open="modals.delete.show"
            :processing="modals.delete.processing"
            @close="toggleDeleteModal"
            @confirm="confirmDelete"
        />

        <create-team-slide-in
            :is-open="modals.create.show"
            v-model:data="modals.create.data"
            :error="modals.create.error"
            :can-submit="canSubmitCreateTeam"
            :processing="modals.create.processing"
            @close="toggleCreateModal"
            @confirm="confirmCreate()"
        />

        <manage-team-grants-slide-in
            :is-open="grantsPanel.show"
            :team-name="grantsPanel.team?.name"
            :grants="grantsPanel.grants"
            :scope-label="scopeLabel"
            @close="closeGrantsPanel"
            @add-grant="openAddGrantModal"
            @revoke-grant="revokeGrantConfirm"
        />

        <add-team-grant-slide-in
            :is-open="grantModal.show"
            v-model:data="grantModal.data"
            :error="grantModal.error"
            :processing="grantModal.processing"
            :role-description="grantRoleDescription"
            :can-submit="canSubmitGrant"
            :team-name="grantsPanel.team?.name"
            :projects="projects"
            :selected-project-environments="selectedProjectEnvironments"
            @close="closeAddGrantModal"
            @confirm="submitGrant"
        />

        <revoke-team-grant-modal
            :is-open="revokeModal.show"
            :grant="revokeModal.grant"
            :team-name="grantsPanel.team?.name"
            :processing="revokeModal.processing"
            :scope-label="scopeLabel"
            @close="closeRevokeModal"
            @confirm="confirmRevoke"
        />
    </main>
</template>

<script setup>
import { ref, reactive, computed, watch, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import AdminHeaderTabs from '@/screens/_components/admin/HeaderTabs.vue'
import DeleteTeamModal from '@/screens/trusted/admin/modals/DeleteTeamModal.vue'
import CreateTeamSlideIn from '@/screens/trusted/admin/modals/CreateTeamSlideIn.vue'
import ManageTeamGrantsSlideIn from '@/screens/trusted/admin/modals/ManageTeamGrantsSlideIn.vue'
import AddTeamGrantSlideIn from '@/screens/trusted/admin/modals/AddTeamGrantSlideIn.vue'
import RevokeTeamGrantModal from '@/screens/trusted/admin/modals/RevokeTeamGrantModal.vue'
import { useSessionStore } from '@/stores/session'
import TeamService from '@/services/team.service'
import { errorMessage } from '@/utils/notifications'
import { removeBy, upsertBy } from '@/utils/list-mutations'
import { usePermissions, PERMISSIONS } from '@/composables/usePermissions'

const router = useRouter()
const sessionStore = useSessionStore()
const { can, isAdmin } = usePermissions()

// State
const loading = ref(false)
const teams = ref([])
const teamGrants = ref({})
const teamGrantsLoading = ref({})

const modals = reactive({
    create: {
        error: null,
        show: false,
        processing: false,
        data: {
            name: null,
            description: null,
        },
    },
    delete: {
        show: false,
        ulid: null,
        processing: false,
    },
})

// Grants panel state
const grantsPanel = reactive({
    show: false,
    team: null,
    grants: [],
})

const grantModal = reactive({
    show: false,
    processing: false,
    error: null,
    data: {
        role: '',
        scope_type: '',
        selectedProjectUlid: '',
        selectedEnvironmentUlid: '',
    },
})

const revokeModal = reactive({
    show: false,
    processing: false,
    grant: null,
})

// Computed
const projects = computed(() => sessionStore.data?.projects?.data ?? [])

const selectedProjectEnvironments = computed(() => {
    if (!grantModal.data.selectedProjectUlid) return []
    const project = projects.value.find(
        (p) => p.ulid === grantModal.data.selectedProjectUlid
    )
    return project?.environments?.data ?? []
})

const grantRoleDescription = computed(() => {
    switch (grantModal.data.role) {
        case 'admin':
            return 'Full access to all features and settings, including user management and billing.'
        case 'editor':
            return 'Can create, update, and delete projects, flags, targets, segments, and webhooks.'
        case 'viewer':
            return 'Read-only access to projects, flags, targets, and segments.'
        default:
            return ''
    }
})

const canSubmitGrant = computed(() => {
    if (!grantModal.data.role || !grantModal.data.scope_type) return false
    if (
        grantModal.data.role === 'admin' &&
        grantModal.data.scope_type === 'environment'
    )
        return false
    if (
        grantModal.data.scope_type === 'project' &&
        !grantModal.data.selectedProjectUlid
    )
        return false
    if (grantModal.data.scope_type === 'environment') {
        if (
            !grantModal.data.selectedProjectUlid ||
            !grantModal.data.selectedEnvironmentUlid
        )
            return false
    }
    return true
})

const createTeamName = computed(() => modals.create.data.name?.trim() ?? '')
const canSubmitCreateTeam = computed(
    () => createTeamName.value.length > 0 && !modals.create.processing
)

// Watchers
watch(
    () => grantModal.data.role,
    (newRole) => {
        if (
            newRole === 'admin' &&
            grantModal.data.scope_type === 'environment'
        ) {
            grantModal.data.scope_type = 'account'
        }
    }
)

watch(
    () => grantModal.data.scope_type,
    () => {
        grantModal.data.selectedProjectUlid = ''
        grantModal.data.selectedEnvironmentUlid = ''
    }
)

watch(
    () => grantModal.data.selectedProjectUlid,
    () => {
        grantModal.data.selectedEnvironmentUlid = ''
    }
)

// Methods
const refresh = async () => {
    loading.value = true
    try {
        const response = await TeamService.list()
        teams.value = response.data

        // Preserve only in-scope cached grant payloads for existing teams.
        const currentTeamUlids = new Set(teams.value.map((team) => team.ulid))
        teamGrants.value = Object.fromEntries(
            Object.entries(teamGrants.value).filter(([teamUlid]) =>
                currentTeamUlids.has(teamUlid)
            )
        )
        teamGrantsLoading.value = Object.fromEntries(
            Object.entries(teamGrantsLoading.value).filter(([teamUlid]) =>
                currentTeamUlids.has(teamUlid)
            )
        )

        // Fetch grants for all teams in batches to avoid overwhelming the API.
        const BATCH_SIZE = 5
        const teamUlids = teams.value.map((t) => t.ulid)
        for (let i = 0; i < teamUlids.length; i += BATCH_SIZE) {
            await Promise.all(
                teamUlids
                    .slice(i, i + BATCH_SIZE)
                    .map((ulid) => fetchTeamGrants(ulid, { force: true }))
            )
        }
    } catch (error) {
        errorMessage(error)
    } finally {
        loading.value = false
    }
}

const resetCreateModal = () => {
    modals.create.data.name = null
    modals.create.data.description = null
    modals.create.error = null
    modals.create.processing = false
}

const toggleCreateModal = () => {
    modals.create.show = !modals.create.show
    if (!modals.create.show) {
        resetCreateModal()
    }
}

const toggleDeleteModal = () => {
    modals.delete.show = !modals.delete.show
    if (!modals.delete.show) {
        modals.delete.ulid = null
        modals.delete.processing = false
    }
}

const openCreateModal = () => {
    toggleCreateModal()
}

const openDeleteModal = (ulid) => {
    modals.delete.ulid = ulid
    toggleDeleteModal()
}

const confirmCreate = async () => {
    if (!canSubmitCreateTeam.value) return

    const createPayload = {
        ...modals.create.data,
        name: createTeamName.value,
    }

    modals.create.processing = true
    try {
        const created = await TeamService.create(createPayload)
        toggleCreateModal()
        // Navigate to the newly created team
        await router.push({
            name: 'AdminTeamDetailsScreen',
            params: { teamUlid: created.data.ulid },
        })
    } catch (error) {
        modals.create.error = error
    } finally {
        modals.create.processing = false
    }
}

const confirmDelete = async () => {
    modals.delete.processing = true
    try {
        // Capture the ulid first: closing the modal clears it.
        const teamUlid = modals.delete.ulid
        await TeamService.remove(teamUlid)
        toggleDeleteModal()
        teams.value = removeBy(teams.value, teamUlid)
        delete teamGrants.value[teamUlid]
        delete teamGrantsLoading.value[teamUlid]
    } catch (error) {
        errorMessage(error)
    } finally {
        modals.delete.processing = false
    }
}

// Lifecycle
onMounted(() => {
    refresh()
})

// Scope label helper
function scopeLabel(grant) {
    if (grant.scope_type === 'account') return 'Account'
    if (grant.scope_type === 'project') {
        const project = projects.value.find((p) => p.ulid === grant.scope_ulid)
        return project ? project.name : 'Project'
    }
    if (grant.scope_type === 'environment') {
        for (const project of projects.value) {
            const env = project.environments?.data?.find(
                (e) => e.ulid === grant.scope_ulid
            )
            if (env) return `${project.name} / ${env.name}`
        }
        return 'Environment'
    }
    return grant.scope_type
}

// Grants panel methods
async function openGrantsPanel(team) {
    grantsPanel.team = team
    grantsPanel.grants = teamGrants.value[team.ulid] ?? []
    grantsPanel.show = true
    await fetchTeamGrants(team.ulid)
}

function closeGrantsPanel() {
    grantsPanel.show = false
    grantsPanel.team = null
    grantsPanel.grants = []
}

async function fetchTeamGrants(teamUlid, options = {}) {
    const { force = false } = options

    if (!force && teamGrants.value[teamUlid] !== undefined) {
        if (grantsPanel.team?.ulid === teamUlid) {
            grantsPanel.grants = teamGrants.value[teamUlid]
        }
        return
    }

    if (teamGrantsLoading.value[teamUlid]) {
        return
    }

    teamGrantsLoading.value[teamUlid] = true

    try {
        const response = await TeamService.listGrants(teamUlid)
        const grants = response.data ?? []
        teamGrants.value[teamUlid] = grants
        if (grantsPanel.team?.ulid === teamUlid) {
            grantsPanel.grants = grants
        }
    } catch (err) {
        if (grantsPanel.team?.ulid === teamUlid) {
            grantsPanel.grants = []
        }
        errorMessage(err)
    } finally {
        teamGrantsLoading.value[teamUlid] = false
    }
}

// Apply one change to a team's cached grants. The grants panel shares that
// array while it shows the team, so it is updated too.
function changeTeamGrants(teamUlid, change) {
    teamGrants.value[teamUlid] = change(teamGrants.value[teamUlid] ?? [])
    if (grantsPanel.team?.ulid === teamUlid) {
        grantsPanel.grants = teamGrants.value[teamUlid]
    }
}

function openAddGrantModal() {
    grantModal.show = true
    grantModal.error = null
    grantModal.processing = false
    grantModal.data.role = ''
    grantModal.data.scope_type = ''
    grantModal.data.selectedProjectUlid = ''
    grantModal.data.selectedEnvironmentUlid = ''
}

function closeAddGrantModal() {
    grantModal.show = false
}

async function submitGrant() {
    if (!grantsPanel.team) return

    grantModal.processing = true
    grantModal.error = null

    const accountUlid = sessionStore.data?.account?.data?.ulid
    let scopeUlid = accountUlid

    if (grantModal.data.scope_type === 'project') {
        scopeUlid = grantModal.data.selectedProjectUlid
    } else if (grantModal.data.scope_type === 'environment') {
        scopeUlid = grantModal.data.selectedEnvironmentUlid
    }

    const teamUlid = grantsPanel.team.ulid
    try {
        const response = await TeamService.createGrant(teamUlid, {
            role: grantModal.data.role,
            scope_type: grantModal.data.scope_type,
            scope_ulid: scopeUlid,
        })
        closeAddGrantModal()
        changeTeamGrants(teamUlid, (grants) =>
            upsertBy(grants, response.data, 'ulid')
        )
    } catch (err) {
        grantModal.error = err
    } finally {
        grantModal.processing = false
    }
}

function revokeGrantConfirm(grant) {
    revokeModal.grant = grant
    revokeModal.show = true
    revokeModal.processing = false
}

function closeRevokeModal() {
    revokeModal.show = false
    revokeModal.grant = null
}

async function confirmRevoke() {
    if (!grantsPanel.team || !revokeModal.grant) return
    revokeModal.processing = true
    const teamUlid = grantsPanel.team.ulid
    const grantUlid = revokeModal.grant.ulid
    try {
        await TeamService.revokeGrant(teamUlid, grantUlid)
        closeRevokeModal()
        changeTeamGrants(teamUlid, (grants) => removeBy(grants, grantUlid))
    } catch (err) {
        errorMessage(err)
    } finally {
        revokeModal.processing = false
    }
}
</script>
